import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Eye, Pencil, Megaphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatJamaicaDate as formatDate } from "@/lib/datetime";

interface AnnouncementFeature {
  emoji: string;
  title: string;
  description: string;
}

interface Announcement {
  id: string;
  title: string;
  subtitle: string;
  features: AnnouncementFeature[];
  isActive: boolean;
  createdAt: string;
}

const EMPTY_FEATURE: AnnouncementFeature = { emoji: "", title: "", description: "" };

function buildEmpty(): Partial<Announcement> {
  return { title: "", subtitle: "", features: [{ ...EMPTY_FEATURE }], isActive: false };
}

function FeaturePreview({ announcement }: { announcement: Partial<Announcement> }) {
  return (
    <div className="border rounded-lg overflow-hidden text-sm max-w-sm mx-auto">
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 px-5 pt-6 pb-5 text-center text-white">
        <div className="text-3xl mb-2">🎉</div>
        <p className="font-bold text-base leading-tight">{announcement.title || "Title"}</p>
        {announcement.subtitle && (
          <p className="text-white/80 text-xs mt-1">{announcement.subtitle}</p>
        )}
      </div>
      {announcement.features && announcement.features.length > 0 && (
        <div className="px-4 py-3 space-y-2.5">
          {announcement.features.filter(f => f.title).map((f, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="text-xl flex-shrink-0 leading-tight">{f.emoji || "•"}</span>
              <div>
                <p className="font-semibold text-xs">{f.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{f.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="px-4 pb-4 pt-1">
        <div className="w-full h-8 bg-primary rounded text-primary-foreground text-xs flex items-center justify-center font-medium">
          Got it! ✨
        </div>
      </div>
    </div>
  );
}

function AnnouncementForm({
  initial,
  onSave,
  onCancel,
  isSaving,
}: {
  initial: Partial<Announcement>;
  onSave: (data: Partial<Announcement>) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [form, setForm] = useState<Partial<Announcement>>(initial);
  const [showPreview, setShowPreview] = useState(false);

  function setField<K extends keyof Announcement>(key: K, value: Announcement[K]) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function setFeature(idx: number, patch: Partial<AnnouncementFeature>) {
    setForm(f => {
      const features = [...(f.features || [])];
      features[idx] = { ...features[idx], ...patch };
      return { ...f, features };
    });
  }

  function addFeature() {
    setForm(f => ({ ...f, features: [...(f.features || []), { ...EMPTY_FEATURE }] }));
  }

  function removeFeature(idx: number) {
    setForm(f => ({ ...f, features: (f.features || []).filter((_, i) => i !== idx) }));
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-1">
          <Label>Title *</Label>
          <Input value={form.title || ""} onChange={e => setField("title", e.target.value)} placeholder="What's New in EduFocal 🎉" />
        </div>
        <div className="col-span-2 space-y-1">
          <Label>Subtitle</Label>
          <Input value={form.subtitle || ""} onChange={e => setField("subtitle", e.target.value)} placeholder="Here's what we've been working on for you" />
        </div>
      </div>

      {/* Features */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Feature bullets</Label>
          <Button type="button" variant="outline" size="sm" onClick={addFeature} className="h-7 text-xs">
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
        {(form.features || []).map((f, i) => (
          <div key={i} className="border rounded-md p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input className="w-16 text-center" value={f.emoji} onChange={e => setFeature(i, { emoji: e.target.value })} placeholder="🔥" />
              <Input className="flex-1" value={f.title} onChange={e => setFeature(i, { title: e.target.value })} placeholder="Feature title" />
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive" onClick={() => removeFeature(i)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Textarea value={f.description} onChange={e => setFeature(i, { description: e.target.value })} placeholder="Short description..." className="min-h-[56px] text-sm" />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Switch
          checked={!!form.isActive}
          onCheckedChange={v => setField("isActive", v)}
          id="isActive"
        />
        <Label htmlFor="isActive" className="cursor-pointer">
          Publish immediately (makes this the active announcement)
        </Label>
      </div>

      <div className="flex items-center justify-between pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(!showPreview)}>
          <Eye className="h-3.5 w-3.5 mr-1.5" /> {showPreview ? "Hide" : "Preview"}
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={isSaving || !form.title?.trim()}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {showPreview && (
        <div className="pt-2">
          <p className="text-xs text-muted-foreground mb-3 text-center">Preview</p>
          <FeaturePreview announcement={form} />
        </div>
      )}
    </div>
  );
}

export default function AdminAnnouncements() {
  const { toast } = useToast();
  const [editing, setEditing] = useState<Partial<Announcement> | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: announcements = [], isLoading } = useQuery<Announcement[]>({
    queryKey: ["/api/admin/announcements"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/announcements");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Announcement>) => {
      const res = await apiRequest("POST", "/api/admin/announcements", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/announcements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/announcements/active"] });
      setIsCreating(false);
      toast({ title: "Announcement created" });
    },
    onError: () => toast({ title: "Failed to create", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Announcement> }) => {
      const res = await apiRequest("PATCH", `/api/admin/announcements/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/announcements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/announcements/active"] });
      setEditing(null);
      toast({ title: "Announcement updated" });
    },
    onError: () => toast({ title: "Failed to update", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/announcements/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/announcements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/announcements/active"] });
      toast({ title: "Announcement deleted" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const toggleActive = (ann: Announcement) => {
    updateMutation.mutate({ id: ann.id, data: { isActive: !ann.isActive } });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Announcements</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage "What's New" popups shown to logged-in users</p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> New Announcement
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : announcements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Megaphone className="h-8 w-8 mx-auto mb-3 opacity-30" />
            <p>No announcements yet. Create one to show users what's new.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements.map((ann) => (
            <Card key={ann.id} className={ann.isActive ? "border-green-500 dark:border-green-600" : ""}>
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CardTitle className="text-base">{ann.title}</CardTitle>
                      {ann.isActive && <Badge className="bg-green-500 text-white text-xs">Active</Badge>}
                    </div>
                    {ann.subtitle && <p className="text-sm text-muted-foreground mt-0.5">{ann.subtitle}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {ann.features?.length || 0} feature bullet{(ann.features?.length || 0) !== 1 ? "s" : ""} · {formatDate(ann.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => toggleActive(ann)}
                      disabled={updateMutation.isPending}
                    >
                      {ann.isActive ? "Deactivate" : "Set Active"}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(ann)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => deleteMutation.mutate(ann.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={isCreating} onOpenChange={v => { if (!v) setIsCreating(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Announcement</DialogTitle>
          </DialogHeader>
          <AnnouncementForm
            initial={buildEmpty()}
            onSave={(data) => createMutation.mutate(data)}
            onCancel={() => setIsCreating(false)}
            isSaving={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={v => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
          </DialogHeader>
          {editing && (
            <AnnouncementForm
              initial={editing}
              onSave={(data) => updateMutation.mutate({ id: editing.id!, data })}
              onCancel={() => setEditing(null)}
              isSaving={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
